import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Mail,
  Plus,
  BarChart3,
  Users,
  Clock,
  Zap,
  Target,
  Send,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Calendar,
  Filter,
  Search,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Activity,
  Tag,
  Sparkles,
  Wand2,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import { useModal, ConfirmModal, EmailCampaignModal, EmailTriggerModal, EmailTemplateModal, EmailTemplatePreviewModal, EmailCampaignPreviewModal } from '@components/modal';
import PageInfoButton from '@/components/PageInfoButton';

interface EmailCampaign {
  id: number;
  name: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled';
  template_id: number;
  trigger_id?: number;
  recipient_count: number;
  sent_count: number;
  open_rate: number;
  click_rate: number;
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
}

interface EmailTrigger {
  id: number;
  name: string;
  type: 'event' | 'schedule' | 'condition' | 'workflow';
  event_type?: string;
  schedule_config?: any;
  condition_config?: any;
  is_active: boolean;
  created_at: string;
}

interface EmailTemplate {
  id: number;
  template_key: string;
  name: string;
  category: 'system' | 'compliance' | 'expiry' | 'notification' | 'marketing' | 'custom';
  subject: string;
  html_content: string;
  text_content: string | null;
  variables: string[];
  required_variables: string[] | null;
  is_active: boolean;
  is_system: boolean;
  is_editable: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

const EmailCenter: React.FC = () => {
  const { isLoading: authLoading } = useAuth();
  const { openModal, closeModal } = useModal();

  const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'templates' | 'triggers' | 'analytics' | 'direct'>(() => {
    const saved = localStorage.getItem('emailCenterActiveTab');
    // 'settings' tab was moved to System Settings — default to overview if stored
    if (saved === 'settings' || !saved) return 'overview';
    return saved as any;
  });

  // Save active tab to localStorage
  useEffect(() => {
    localStorage.setItem('emailCenterActiveTab', activeTab);
  }, [activeTab]);

  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [triggers, setTriggers] = useState<EmailTrigger[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<string>('all');

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch('/api/admin/users?page=1&limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      } else {
        console.error('Failed to load users');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSendDirectEmail = async () => {
    if (!directEmail.to || !directEmail.subject || !directEmail.body) {
      notify.error('Please fill in all fields');
      return;
    }

    setSendingDirect(true);
    try {
      const response = await fetch('/api/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          to: directEmail.to,
          subject: directEmail.subject,
          body: directEmail.body,
          queue: false
        })
      });

      if (response.ok) {
        notify.success('Email sent successfully');
        setDirectEmail({ to: '', subject: '', body: '' });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending direct email:', error);
      notify.error('Failed to send email');
    } finally {
      setSendingDirect(false);
    }
  };

  // Direct email state
  const [directEmail, setDirectEmail] = useState({
    to: '',
    subject: '',
    body: ''
  });
  const [sendingDirect, setSendingDirect] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // AI assist state (direct email)
  const [showDirectAI, setShowDirectAI] = useState(false);
  const [directAIPrompt, setDirectAIPrompt] = useState('');
  const [isDirectAILoading, setIsDirectAILoading] = useState(false);

  const callDirectEmailAI = async (instruction: string) => {
    setIsDirectAILoading(true);
    try {
      const existing = directEmail.body ? `\n\nExisting draft:\nSubject: ${directEmail.subject}\n${directEmail.body}` : '';
      const prompt = `Write a professional HTML email for a compliance platform. Task: ${instruction}${existing}

Reply ONLY with this JSON (no markdown fences, no extra text):
{"subject":"<subject line>","body":"<HTML email body — use simple inline-styled HTML, no <html>/<head>/<body> tags>"}`;

      const response = await fetch('/api/ai/orchestrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ user_message: prompt }),
      });
      if (!response.ok) throw new Error('failed');
      const data = await response.json();
      const text: string = data.message || data.response || data.answer || '';

      let parsed: { subject?: string; body?: string } | null = null;
      try { parsed = JSON.parse(text); } catch {}
      if (!parsed) { try { parsed = JSON.parse(text.replace(/```json|```/g, '').trim()); } catch {} }
      if (!parsed) { const m = text.match(/\{[\s\S]*?\}/); if (m) { try { parsed = JSON.parse(m[0]); } catch {} } }
      if (!parsed && text.length > 10) {
        const lines = text.split('\n');
        parsed = { subject: directEmail.subject || lines[0]?.slice(0, 80), body: text };
      }

      if (parsed && (parsed.subject || parsed.body)) {
        setDirectEmail(prev => ({
          ...prev,
          ...(parsed!.subject ? { subject: parsed!.subject! } : {}),
          ...(parsed!.body ? { body: parsed!.body! } : {}),
        }));
        setDirectAIPrompt('');
        setShowDirectAI(false);
        notify.success('AI generated your email');
      } else {
        notify.error('Could not generate email. Try rephrasing.');
      }
    } catch {
      notify.error('AI assist failed. Please try again.');
    } finally {
      setIsDirectAILoading(false);
    }
  };

  // Campaign lifecycle actions
  const handleLaunchCampaign = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/admin/email-campaigns/${campaignId}/launch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) { notify.success('Campaign launched!'); loadCampaigns(); }
      else { const e = await response.json(); notify.error(e.message || 'Failed to launch campaign'); }
    } catch { notify.error('Failed to launch campaign'); }
  };

  const handlePauseCampaign = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/admin/email-campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) { notify.success('Campaign paused'); loadCampaigns(); }
      else { const e = await response.json(); notify.error(e.message || 'Failed to pause campaign'); }
    } catch { notify.error('Failed to pause campaign'); }
  };

  const handleResumeCampaign = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/admin/email-campaigns/${campaignId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) { notify.success('Campaign resumed'); loadCampaigns(); }
      else { const e = await response.json(); notify.error(e.message || 'Failed to resume campaign'); }
    } catch { notify.error('Failed to resume campaign'); }
  };

  const handleCancelCampaign = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/admin/email-campaigns/${campaignId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) { notify.success('Campaign cancelled'); loadCampaigns(); }
      else { const e = await response.json(); notify.error(e.message || 'Failed to cancel campaign'); }
    } catch { notify.error('Failed to cancel campaign'); }
  };

  // Email settings state
  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'direct') {
      loadUsers();
    }
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadCampaigns(),
        loadTriggers(),
        loadTemplates()
      ]);
    } catch (error) {
      console.error('Error loading email marketing data:', error);
      notify.error('Failed to load email marketing data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await fetch('/api/admin/email-campaigns', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(Array.isArray(data.data) ? data.data : []);
      } else if (response.status === 404) {
        // API not implemented yet, show empty state
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      // Show empty state on error
      setCampaigns([]);
    }
  };

  const loadTriggers = async () => {
    try {
      const response = await fetch('/api/admin/email-triggers?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTriggers(Array.isArray(data.data) ? data.data : []);
      } else if (response.status === 404) {
        // API not implemented yet, show empty state
        setTriggers([]);
      }
    } catch (error) {
      console.error('Error loading triggers:', error);
      // Show empty state on error
      setTriggers([]);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/admin/email-templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const templates = Array.isArray(data.data) ? data.data : [];

        // Parse JSON fields that come as strings from the database
        const processedTemplates = templates.map((template: any) => ({
          ...template,
          variables: Array.isArray(template.variables)
            ? template.variables
            : (typeof template.variables === 'string'
                ? JSON.parse(template.variables)
                : []),
          required_variables: Array.isArray(template.required_variables)
            ? template.required_variables
            : (typeof template.required_variables === 'string'
                ? JSON.parse(template.required_variables)
                : null)
        }));

        setTemplates(processedTemplates);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    }
  };

  const handleCreateCampaign = async (campaignData: Partial<EmailCampaign>) => {
    try {
      const response = await fetch('/api/admin/email-campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(campaignData),
      });

      if (response.ok) {
        notify.success('Campaign created successfully!');
        loadCampaigns();
      } else if (response.status === 404) {
        notify.info('Campaign creation feature coming soon! API endpoints need to be implemented.');
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      notify.info('Campaign creation feature coming soon! API endpoints need to be implemented.');
    }
  };

  const handleCreateTrigger = async (triggerData: any) => {
    try {
      const response = await fetch('/api/admin/email-triggers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(triggerData),
      });

      if (response.ok) {
        notify.success('Trigger created successfully!');
        loadTriggers();
      } else if (response.status === 404) {
        notify.info('Trigger creation feature coming soon! API endpoints need to be implemented.');
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to create trigger');
      }
    } catch (error) {
      console.error('Error creating trigger:', error);
      notify.info('Trigger creation feature coming soon! API endpoints need to be implemented.');
    }
  };

  const handlePreviewCampaign = (campaign: EmailCampaign) => {
    openModal({
      component: EmailCampaignPreviewModal,
      props: {
        campaign: campaign,
        onClose: () => {},
      },
      options: { size: 'large' }
    });
  };

  const handleEditCampaign = (campaign: EmailCampaign) => {
    const modalId = openModal({
      component: EmailCampaignModal,
      props: {
        onSubmit: handleUpdateCampaign,
        selectedCampaign: campaign,
        templates: templates,
        triggers: triggers,
        onClose: () => closeModal(modalId),
      },
      options: { size: 'large' }
    });
  };

  const handleUpdateCampaign = async (campaignData: Partial<EmailCampaign>) => {
    if (!campaignData.id) return;

    try {
      const response = await fetch(`/api/admin/email-campaigns/${campaignData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(campaignData),
      });

      if (response.ok) {
        notify.success('Campaign updated successfully!');
        loadCampaigns();
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to update campaign');
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      notify.error('Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/admin/email-campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        notify.success('Campaign deleted successfully!');
        loadCampaigns();
      } else if (response.status === 404) {
        // Campaign not found in database, remove from UI anyway
        setCampaigns(prev => prev.filter(c => c.id !== campaignId));
        notify.success('Campaign removed from list!');
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      notify.error('Failed to delete campaign');
    }
  };

  const handleEditTrigger = (trigger: any) => {
    const modalId = openModal({
      component: EmailTriggerModal,
      props: {
        onSubmit: handleUpdateTrigger,
        selectedTrigger: trigger,
        onClose: () => closeModal(modalId),
      },
      options: { size: 'large' }
    });
  };

  const handleUpdateTrigger = async (triggerData: any) => {
    if (!triggerData.id) return;

    try {
      const response = await fetch(`/api/admin/email-triggers/${triggerData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(triggerData),
      });

      if (response.ok) {
        notify.success('Trigger updated successfully!');
        loadTriggers();
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to update trigger');
      }
    } catch (error) {
      console.error('Error updating trigger:', error);
      notify.error('Failed to update trigger');
    }
  };

  const handleDeleteTrigger = async (triggerId: number) => {
    try {
      const response = await fetch(`/api/admin/email-triggers/${triggerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        notify.success('Trigger deleted successfully!');
        loadTriggers();
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to delete trigger');
      }
    } catch (error) {
      console.error('Error deleting trigger:', error);
      notify.error('Failed to delete trigger');
    }
  };

  // Template management functions
  const handleCreateTemplate = async (templateData: any) => {
    try {
      const response = await fetch('/api/admin/email-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        notify.success('Template created successfully!');
        loadTemplates();
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      notify.error('Failed to create template');
    }
  };

  const handleUpdateTemplate = async (id: number, templateData: any) => {
    try {
      const response = await fetch(`/api/admin/email-templates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        notify.success('Template updated successfully!');
        loadTemplates();
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      notify.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    try {
      const response = await fetch(`/api/admin/email-templates/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        notify.success('Template deleted successfully!');
        loadTemplates();
      } else {
        const error = await response.json();
        notify.error(error.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      notify.error('Failed to delete template');
    }
  };

  const handlePreviewTemplate = (template: EmailTemplate) => {
    openModal({
      component: EmailTemplatePreviewModal,
      props: { template },
      options: { size: 'large' }
    });
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    const modalId = openModal({
      component: EmailTemplateModal,
      props: {
        selectedTemplate: template,
        onSubmit: (data: any) => handleUpdateTemplate(template.id, data),
        onClose: () => closeModal(modalId)
      },
      options: { size: 'large' }
    });
  };

  // Filter templates based on search and category
  const filteredTemplates = Array.isArray(templates) ? templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(templateSearchTerm.toLowerCase()) ||
                         template.subject.toLowerCase().includes(templateSearchTerm.toLowerCase());
    const matchesCategory = templateCategoryFilter === 'all' || template.category === templateCategoryFilter;
    return matchesSearch && matchesCategory;
  }) : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800 border border-green-200';
      case 'sending': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'paused': return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border border-red-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle size={16} />;
      case 'sending': return <Send size={16} />;
      case 'scheduled': return <Clock size={16} />;
      case 'paused': return <Pause size={16} />;
      case 'cancelled': return <AlertCircle size={16} />;
      case 'draft': return <Edit size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    totalCampaigns: campaigns.length,
    activeCampaigns: campaigns.filter(c => c.status === 'sending' || c.status === 'scheduled').length,
    totalSent: campaigns.reduce((sum, c) => sum + c.sent_count, 0),
    averageOpenRate: campaigns.length > 0 ?
      (campaigns.reduce((sum, c) => sum + c.open_rate, 0) / campaigns.length).toFixed(1) : 0,
    averageClickRate: campaigns.length > 0 ?
      (campaigns.reduce((sum, c) => sum + c.click_rate, 0) / campaigns.length).toFixed(1) : 0
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

        {/* Page Header */}
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Email Center</h1><PageInfoButton /></div>
          <p className="text-sm text-slate-500 mt-1">Manage campaigns, templates, and automation</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-1 border-b border-slate-100">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'campaigns', label: 'Campaigns', icon: Send },
            { id: 'direct', label: 'Direct Email', icon: Mail },
            { id: 'templates', label: 'Templates', icon: Mail },
            { id: 'triggers', label: 'Triggers', icon: Zap },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-600">Total Campaigns</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.totalCampaigns}</p>
                  </div>
                  <div className="p-2.5 bg-blue-50 rounded-lg">
                    <Send className="text-blue-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-600">Active Campaigns</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.activeCampaigns}</p>
                  </div>
                  <div className="p-2.5 bg-green-50 rounded-lg">
                    <Play className="text-green-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-600">Avg Open Rate</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.averageOpenRate}%</p>
                  </div>
                  <div className="p-2.5 bg-purple-50 rounded-lg">
                    <Eye className="text-purple-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-600">Avg Click Rate</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.averageClickRate}%</p>
                  </div>
                  <div className="p-2.5 bg-orange-50 rounded-lg">
                    <Target className="text-orange-600" size={24} />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab('templates')}
                  className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="p-2.5 bg-blue-50 rounded-lg">
                    <Mail className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Create Template</h4>
                    <p className="text-sm text-slate-600">Design email templates</p>
                  </div>
                </button>

                <button
                  onClick={() => setActiveTab('triggers')}
                  className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="p-2.5 bg-green-50 rounded-lg">
                    <Zap className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Setup Triggers</h4>
                    <p className="text-sm text-slate-600">Automate email sending</p>
                  </div>
                </button>

                <button
                  onClick={() => setActiveTab('campaigns')}
                  className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="p-2.5 bg-purple-50 rounded-lg">
                    <Target className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Launch Campaign</h4>
                    <p className="text-sm text-slate-600">Send targeted emails</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Email Campaigns</h2>
                  <p className="text-slate-600">Create and manage your email marketing campaigns</p>
                </div>
                <button
                  onClick={() => {
                    openModal({
                      component: EmailCampaignModal,
                      props: {
                        onSubmit: handleCreateCampaign,
                        selectedCampaign: null,
                        templates: templates,
                        triggers: triggers,
                      },
                      options: { size: 'large' }
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  New Campaign
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search campaigns..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Status</label>
                  <div className="relative">
                    <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="pl-12 pr-10 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white cursor-pointer"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="sending">Sending</option>
                      <option value="sent">Sent</option>
                      <option value="paused">Paused</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaigns List */}
            <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">Email Campaigns</h3>
                    <p className="text-sm text-slate-500">All marketing campaigns</p>
                  </div>
                  <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-lg text-sm font-medium">
                    {filteredCampaigns.length}
                  </span>
                </div>
              </div>

              {isLoading ? (
                <div className="p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
                  <p className="text-slate-600 mt-2 font-medium">Loading campaigns...</p>
                </div>
              ) : filteredCampaigns.length === 0 ? (
                <div className="p-16">
                  <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-slate-100 mb-4">
                    <Send className="text-slate-400" size={40} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No campaigns found</h3>
                  <p className="text-lg text-slate-600 max-w-md mx-auto">
                    {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first email campaign to get started'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredCampaigns.map(campaign => (
                    <div key={campaign.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-slate-900">{campaign.name}</h3>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(campaign.status)}`}>
                              {getStatusIcon(campaign.status)}
                              {campaign.status}
                            </span>
                          </div>
                          <p className="text-slate-600 mb-3">{campaign.subject}</p>
                          <div className="flex items-center gap-6 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Users size={14} />
                              {campaign.recipient_count} recipients
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye size={14} />
                              {campaign.open_rate.toFixed(1)}% open rate
                            </span>
                            <span className="flex items-center gap-1">
                              <Target size={14} />
                              {campaign.click_rate.toFixed(1)}% click rate
                            </span>
                            {campaign.scheduled_at && (
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {new Date(campaign.scheduled_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {/* Status-aware lifecycle actions */}
                          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                            <button
                              onClick={() => handleLaunchCampaign(campaign.id)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
                              title="Launch campaign"
                            >
                              <Play size={13} /> Launch
                            </button>
                          )}
                          {campaign.status === 'sending' && (
                            <button
                              onClick={() => handlePauseCampaign(campaign.id)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors"
                              title="Pause campaign"
                            >
                              <Pause size={13} /> Pause
                            </button>
                          )}
                          {campaign.status === 'paused' && (
                            <button
                              onClick={() => handleResumeCampaign(campaign.id)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                              title="Resume campaign"
                            >
                              <RefreshCw size={13} /> Resume
                            </button>
                          )}
                          {(campaign.status === 'sending' || campaign.status === 'scheduled' || campaign.status === 'paused') && (
                            <button
                              onClick={() => openModal({
                                component: ConfirmModal,
                                props: {
                                  title: 'Cancel Campaign',
                                  message: `Cancel "${campaign.name}"? This cannot be undone.`,
                                  onConfirm: () => handleCancelCampaign(campaign.id),
                                  confirmText: 'Cancel Campaign',
                                  cancelText: 'Keep',
                                  type: 'danger'
                                }
                              })}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Cancel campaign"
                            >
                              <XCircle size={15} />
                            </button>
                          )}
                          <div className="w-px h-5 bg-slate-200 mx-0.5" />
                          <button
                            onClick={() => handlePreviewCampaign(campaign)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Preview campaign"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditCampaign(campaign)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => {
                              openModal({
                                component: ConfirmModal,
                                props: {
                                  title: 'Delete Campaign',
                                  message: `Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`,
                                  onConfirm: () => handleDeleteCampaign(campaign.id),
                                  confirmText: 'Delete',
                                  cancelText: 'Cancel',
                                  type: 'danger'
                                }
                              });
                            }}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Email Templates</h2>
                  <p className="text-slate-600">Design and manage your email templates</p>
                </div>
                <button
                  onClick={() => {
                    openModal({
                      component: EmailTemplateModal,
                      props: {
                        onSubmit: handleCreateTemplate,
                        selectedTemplate: null
                      },
                      options: { size: 'large' }
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  Create Template
                </button>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search templates..."
                      value={templateSearchTerm}
                      onChange={(e) => setTemplateSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Category</label>
                  <select
                    value={templateCategoryFilter}
                    onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                  >
                    <option value="all">All Categories</option>
                    <option value="system">System</option>
                    <option value="compliance">Compliance</option>
                    <option value="expiry">Expiry</option>
                    <option value="notification">Notification</option>
                    <option value="marketing">Marketing</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map(template => (
                <div key={template.id} className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg">
                      <Mail className="text-blue-600" size={20} />
                    </div>
                    <div className="flex gap-2">
                      {template.is_system && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 border border-orange-200 rounded-full text-xs font-bold">
                          System
                        </span>
                      )}
                      {!template.is_active && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 border border-red-200 rounded-full text-xs font-bold">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className="font-bold text-slate-900 mb-2 text-lg">{template.name}</h3>
                  <p className="text-slate-600 text-sm mb-2">{template.subject}</p>
                  <div className="flex items-center gap-2 mb-4">
                    <Tag size={14} className="text-slate-400" />
                    <span className="text-xs text-slate-500 capitalize font-medium">{template.category}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                    <span className="font-medium">{template.usage_count} uses</span>
                    {template.last_used_at && (
                      <span className="font-medium">Last used: {new Date(template.last_used_at).toLocaleDateString()}</span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handlePreviewTemplate(template)}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Preview template"
                      >
                        <Eye size={16} />
                      </button>
                      {template.is_editable && (
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Edit template"
                        >
                          <Edit size={16} />
                        </button>
                      )}
                      {template.is_editable && (
                        <button
                          onClick={() => {
                            openModal({
                              component: ConfirmModal,
                              props: {
                                title: 'Delete Template',
                                message: `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
                                onConfirm: () => handleDeleteTemplate(template.id),
                                confirmText: 'Delete',
                                cancelText: 'Cancel',
                                type: 'danger'
                              }
                            });
                          }}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete template"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {template.variables && template.variables.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {template.variables.slice(0, 3).map((variable: string, index: number) => (
                            <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-mono font-bold">
                              {variable}
                            </span>
                          ))}
                          {template.variables.length > 3 && (
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 border border-slate-200 rounded text-xs font-bold">
                              +{template.variables.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                      <div className={`px-2 py-1 rounded text-xs font-bold self-start border ${
                        template.is_active
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-red-100 text-red-700 border-red-200'
                      }`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="bg-white rounded-3xl border border-slate-100 p-12">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-slate-100 mb-4">
                  <Mail className="text-slate-400" size={40} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No templates found</h3>
                <p className="text-lg text-slate-600 mb-6">
                  {templateSearchTerm || templateCategoryFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by creating your first email template.'}
                </p>
                <button
                  onClick={() => {
                    openModal({
                      component: EmailTemplateModal,
                      props: {
                        onSubmit: handleCreateTemplate,
                        selectedTemplate: null
                      },
                      options: { size: 'large' }
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  Create Template
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'triggers' && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Email Triggers</h2>
                  <p className="text-slate-600">Automate email sending based on events and conditions</p>
                </div>
                <button
                  onClick={() => {
                    const modalId = openModal({
                      component: EmailTriggerModal,
                      props: {
                        onSubmit: handleCreateTrigger,
                        selectedTrigger: null,
                        onClose: () => closeModal(modalId),
                      },
                      options: { size: 'large' }
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  Create Trigger
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.isArray(triggers) && triggers.map(trigger => (
                <div key={trigger.id} className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-green-50 rounded-lg">
                      <Zap className="text-green-600" size={20} />
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold border ${
                      trigger.is_active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'
                    }`}>
                      {trigger.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 text-lg">{trigger.name}</h3>
                  <p className="text-slate-600 text-sm mb-4 capitalize font-medium">{trigger.type}</p>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span className="font-medium">{trigger.event_type || 'Event-based'}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditTrigger(trigger)}
                        className="p-1 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => {
                          openModal({
                            component: ConfirmModal,
                            props: {
                              title: 'Delete Trigger',
                              message: `Are you sure you want to delete "${trigger.name}"? This action cannot be undone.`,
                              onConfirm: () => handleDeleteTrigger(trigger.id),
                              confirmText: 'Delete',
                              cancelText: 'Cancel',
                              type: 'danger'
                            }
                          });
                        }}
                        className="p-1 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'direct' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Send Direct Email</h2>
                  <p className="text-slate-600">Send a one-off email to any recipient</p>
                </div>
              </div>
            </div>

            {/* Email Form */}
            <div className="bg-white rounded-3xl border border-slate-100 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Compose Email</h3>
                <p className="text-slate-600">Send a direct email to any recipient or select from existing users</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSendDirectEmail(); }} className="space-y-6">
                {/* Recipient Section */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <label className="text-sm font-bold text-slate-700">Recipient</label>
                  </div>

                  <div className="relative">
                    <input
                      type="email"
                      value={directEmail.to}
                      onChange={(e) => setDirectEmail(prev => ({ ...prev, to: e.target.value }))}
                      placeholder="Type email address or select from users..."
                      className="w-full px-4 py-3 pl-12 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      list="user-emails"
                      required
                    />
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <datalist id="user-emails">
                      {users.map(user => (
                        <option key={user.id} value={user.email}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </datalist>
                  </div>

                  {loadingUsers && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                      <div className="animate-spin rounded-full h-3 w-3 border border-slate-300 border-t-slate-500"></div>
                      Loading users...
                    </div>
                  )}

                  {!loadingUsers && users.length > 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      💡 {users.length} users available - start typing to see suggestions
                    </p>
                  )}
                </div>

                {/* Subject Section */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <label className="text-sm font-bold text-slate-700">Subject</label>
                  </div>

                  <input
                    type="text"
                    value={directEmail.subject}
                    onChange={(e) => setDirectEmail(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Enter email subject..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    required
                  />
                </div>

                {/* Message Section */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <label className="text-sm font-bold text-slate-700">Message</label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowDirectAI(v => !v)}
                      className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-full transition-colors"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      AI Assist
                      {showDirectAI ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>

                  <textarea
                    value={directEmail.body}
                    onChange={(e) => setDirectEmail(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Compose your email message... (HTML supported)"
                    rows={10}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical bg-white"
                    required
                  />

                  {/* AI Assist Panel */}
                  {showDirectAI && (
                    <div className="mt-3 border border-indigo-200 rounded-xl bg-indigo-50 p-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                        <Wand2 className="w-4 h-4" />
                        AI Email Assistant
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[
                          'Write a compliance deadline reminder email',
                          'Write a welcome email for a new user',
                          'Notify about a framework update',
                          'Request evidence submission for audit',
                          'Send a monthly compliance status summary',
                        ].map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => callDirectEmailAI(s)}
                            disabled={isDirectAILoading}
                            className="text-xs bg-white border border-indigo-200 text-indigo-700 px-2.5 py-1 rounded-full hover:bg-indigo-100 transition-colors disabled:opacity-50"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={directAIPrompt}
                          onChange={(e) => setDirectAIPrompt(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && directAIPrompt.trim()) callDirectEmailAI(directAIPrompt); }}
                          placeholder="Describe the email you want to write..."
                          className="flex-1 text-sm px-3 py-1.5 border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                          disabled={isDirectAILoading}
                        />
                        <button
                          type="button"
                          onClick={() => directAIPrompt.trim() && callDirectEmailAI(directAIPrompt)}
                          disabled={isDirectAILoading || !directAIPrompt.trim()}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                        >
                          {isDirectAILoading ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <Sparkles className="w-3.5 h-3.5" />}
                          Generate
                        </button>
                      </div>
                      {directEmail.body && (
                        <button
                          type="button"
                          onClick={() => callDirectEmailAI('Rewrite and improve this email to be more professional, clear, and engaging, keeping the same intent')}
                          disabled={isDirectAILoading}
                          className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Rewrite existing content
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between items-center mt-3 text-sm text-slate-500">
                    <span>HTML formatting supported</span>
                    <span>{directEmail.body.length} characters</span>
                  </div>
                </div>

                {/* Send Button */}
                <div className="flex justify-end pt-4 border-t border-slate-200">
                  <button
                    type="submit"
                    disabled={sendingDirect || !directEmail.to.trim() || !directEmail.subject.trim() || !directEmail.body.trim()}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingDirect ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span className="text-lg">Sending Email...</span>
                      </>
                    ) : (
                      <>
                        <Send size={24} />
                        <span className="text-lg">Send Email</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Email Analytics</h2>
                  <p className="text-slate-600">Track performance and engagement metrics</p>
                </div>
                <div className="flex gap-2">
                  <select className="px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer font-medium">
                    <option>Last 7 days</option>
                    <option>Last 30 days</option>
                    <option>Last 90 days</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <h3 className="text-lg font-bold text-slate-900 mb-4">Performance Overview</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 font-medium">Total Emails Sent</span>
                    <span className="font-bold text-slate-900">{stats.totalSent.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 font-medium">Average Open Rate</span>
                    <span className="font-bold text-slate-900">{stats.averageOpenRate}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600 font-medium">Average Click Rate</span>
                    <span className="font-bold text-slate-900">{stats.averageClickRate}%</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
                <h3 className="text-lg font-bold text-slate-900 mb-4">Campaign Status</h3>
                <div className="space-y-3">
                  {['sent', 'sending', 'scheduled', 'draft'].map(status => {
                    const count = campaigns.filter(c => c.status === status).length;
                    return (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(status)}
                          <span className="text-slate-600 font-medium capitalize">{status}</span>
                        </div>
                        <span className="font-bold text-slate-900">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
};

export default EmailCenter;
